// Live report pool: recently used report iframes stay alive (parked off-screen) across in-app
// navigation, so returning to a report is instant and in exactly the state it was left.
// Needs Element.moveBefore (Chromium 133+) to move an iframe without reloading it; elsewhere,
// or when disabled, reports fall back to session state restore (reportSessionState.js).
//
// Env (build time):
//   REACT_APP_PBI_POOL_ENABLED        "true" to enable (default off)
//   REACT_APP_PBI_POOL_MAX_REPORTS    hard cap on parked reports (default 4, 0 disables)
//   REACT_APP_PBI_POOL_MEMORY_FRACTION share of device memory parked reports may use (default 0.12)
//   REACT_APP_PBI_POOL_REPORT_MB      assumed memory per parked report (default 300)
//   REACT_APP_PBI_POOL_IDLE_MINUTES   parked reports are dropped after this long unused (default 20)

/* global powerbi */

const numberEnv = (value, fallback) => {
  const n = parseFloat(value);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
};

const config = {
  enabled: process.env.REACT_APP_PBI_POOL_ENABLED === "true",
  maxReports: numberEnv(process.env.REACT_APP_PBI_POOL_MAX_REPORTS, 4),
  memoryFraction: numberEnv(process.env.REACT_APP_PBI_POOL_MEMORY_FRACTION, 0.12),
  reportMB: numberEnv(process.env.REACT_APP_PBI_POOL_REPORT_MB, 300),
  idleMs: numberEnv(process.env.REACT_APP_PBI_POOL_IDLE_MINUTES, 20) * 60 * 1000,
};

// Parked reports whose token expires sooner than this are dropped rather than reused.
const MIN_TOKEN_LIFE_MS = 60 * 1000;
// Used when the browser doesn't report navigator.deviceMemory.
const DEFAULT_DEVICE_MEMORY_GB = 4;

// Least recently used first (entries are re-inserted when parked).
const entries = new Map();
let host = null;

export const computePoolBudget = ({ deviceMemoryGB, maxReports, memoryFraction, reportMB }) => {
  if (!(reportMB > 0)) return 0;
  const memoryGB = deviceMemoryGB > 0 ? deviceMemoryGB : DEFAULT_DEVICE_MEMORY_GB;
  const byMemory = Math.floor((memoryGB * 1024 * memoryFraction) / reportMB);
  return Math.max(0, Math.min(Math.floor(maxReports), byMemory));
};

export const getPoolBudget = () =>
  computePoolBudget({ deviceMemoryGB: typeof navigator !== "undefined" ? navigator.deviceMemory : undefined, ...config });

const supportsMove = () => typeof Element !== "undefined" && typeof Element.prototype.moveBefore === "function";

export const isPoolEnabled = () => config.enabled && supportsMove() && getPoolBudget() > 0;

const getHost = () => {
  if (host?.isConnected) return host;
  host = document.createElement("div");
  host.setAttribute("data-pbi-pool-host", "");
  host.setAttribute("aria-hidden", "true");
  host.inert = true;
  // Off-screen but laid out at viewport size, so reports don't re-layout while parked.
  host.style.cssText =
    "position:fixed;left:-200vw;top:0;width:100vw;height:100vh;overflow:hidden;visibility:hidden;pointer-events:none;";
  document.body.appendChild(host);
  return host;
};

export const createPoolContainer = () => {
  const container = document.createElement("div");
  container.setAttribute("data-pbi-pool-container", "");
  container.style.cssText = "width:100%;height:100%;";
  return container;
};

// Moves node to the end of parent, keeping a live iframe alive. Returns false if it couldn't
// be moved statefully (the node is left where it was).
export const moveInto = (parent, node) => {
  if (!node.isConnected) {
    parent.appendChild(node);
    return true;
  }
  if (!parent.isConnected || typeof parent.moveBefore !== "function") return false;
  try {
    parent.moveBefore(node, null);
    return true;
  } catch (err) {
    return false;
  }
};

// Keeps a container alive off-screen while its React slot is swapped (e.g. a layout change).
export const holdPoolContainer = (container) => {
  if (container?.isConnected) moveInto(getHost(), container);
};

export const destroyPoolContainer = (container) => {
  if (!container) return;
  try {
    if (typeof powerbi !== "undefined") powerbi.reset(container);
  } catch (err) {
    // Already torn down.
  }
  container.remove();
};

const dropEntry = (entry) => {
  clearTimeout(entry.idleTimer);
  destroyPoolContainer(entry.container);
};

export const evictPooledReport = (reportId) => {
  const entry = entries.get(reportId);
  if (!entry) return;
  entries.delete(reportId);
  dropEntry(entry);
};

const isReusable = (entry) =>
  entry.container.isConnected &&
  entry.container.powerBiEmbed === entry.component &&
  (!(entry.tokenExpiresAt > 0) || entry.tokenExpiresAt - Date.now() > MIN_TOKEN_LIFE_MS);

// Removes and returns the parked report, or null if there is none worth reusing.
export const takePooledReport = (reportId) => {
  const entry = entries.get(reportId);
  if (!entry) return null;
  entries.delete(reportId);
  clearTimeout(entry.idleTimer);
  if (!isPoolEnabled() || !isReusable(entry)) {
    dropEntry(entry);
    return null;
  }
  return entry;
};

// Parks a live report off-screen. Returns false (after destroying it) if it can't be pooled.
// The caller must have removed its own event handlers from the component.
export const parkReport = (reportId, { container, component, tokenExpiresAt }) => {
  evictPooledReport(reportId);
  const entry = { reportId, container, component, tokenExpiresAt, idleTimer: null };
  if (!reportId || !isPoolEnabled() || !container?.isConnected || !moveInto(getHost(), container)) {
    dropEntry(entry);
    return false;
  }
  entry.idleTimer = setTimeout(() => evictPooledReport(reportId), config.idleMs);
  entries.set(reportId, entry);
  const budget = getPoolBudget();
  for (const id of entries.keys()) {
    if (entries.size <= budget) break;
    evictPooledReport(id);
  }
  return entries.has(reportId);
};

export const getPooledReportIds = () => [...entries.keys()];

export const clearPbiEmbedPool = () => {
  for (const id of [...entries.keys()]) evictPooledReport(id);
};

// E2E test hook: introspect/steer the pool from the browser without waiting on real budgets
// or token/idle timers. Stripped from production bundles (dead code eliminated, like window.__mdmGrid).
if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
  window.__pbiEmbedPool = {
    isPoolEnabled,
    getPoolBudget,
    getPooledReportIds,
    getIdleMs: () => config.idleMs,
    // Makes a parked report look like its token is about to expire, so the next return reloads it.
    debugExpireToken: (reportId) => {
      const entry = entries.get(reportId);
      if (entry) entry.tokenExpiresAt = Date.now();
    },
  };
}
